import React, { useEffect, useState, useRef, useCallback } from "react";
import Navbar from "../../../components/Navbar/Navbar";
import "./WorkPhone.scss";
import Progress from "../../../components/Progress/Progress";
import ScrollToTop from "../../../components/ScrollToTop/ScrollToTop";
import { StepPreFooter } from "../../../components/Footer/StepPreFooter";
import { useLocation } from "react-router-dom";
import { getPrevStepLink } from "../../../utils/functions";
import WorkPhoneIcon from "../../../assets/images/step-icon/WorkPhone-icon.png";
import { Controller, useForm } from "react-hook-form";
import { updateContact } from "../../../redux/apiCalls";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Box, FormControl, FormHelperText, TextField } from "@mui/material";
import { formControlCustom, labelCustom, textFieldCustom } from "./style";
import ReactInputMask from "react-input-mask";
import { trimWhiteSpaceObjValue } from "../../../utils/functions";
import { StepBtn } from "../../../components/Button/StepBtn/StepBtn";
import { nextStepDelayTime, phonePattern } from "../../../utils/const";

const WorkPhone = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const currentLoc = useLocation();
  const prevLink = getPrevStepLink(currentLoc.pathname);

  const [loadingButton, setLoadingButton] = useState(false);

  const contactRedux = useSelector((state) => state?.contact?.contact);
  const homePhone = contactRedux?.homePhone ?? "";
  const cellPhone = contactRedux?.cellPhone ?? "";
  const [employerPhone, setEmployerPhone] = useState(
    contactRedux?.workPhone ?? contactRedux?.employerPhone ?? ""
  );

  const windowWidth = window.innerWidth;
  const isMobile = windowWidth < 768;

  const {
    control,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors }
  } = useForm();

  // Memoize validation function to prevent unnecessary re-renders
  const checkPhoneEquality = useCallback((workPhone, comparePhone, phoneType) => {
    if (!workPhone || !comparePhone) return false;
    
    const stripMask = workPhone.replace(/[()-\s_]/g, "");
    const stripCompare = comparePhone.replace(/[()-\s_]/g, "");
    
    if (stripMask && stripMask === stripCompare) {
      setError("employerPhone", {
        type: "duplicate",
        message: `Company phone number must not be the same as ${phoneType} phone number!`
      });
      return true;
    }
    return false;
  }, [setError]);

  // Validate phone number format
  const validatePhoneFormat = useCallback((phone) => {
    if (!phone) return false;
    const cleanPhone = phone.replace(/[()-\s_]/g, "");
    return cleanPhone.length === 10 && /^\d{10}$/.test(cleanPhone);
  }, []);

  const onSubmit = async (data) => {
    if (loadingButton) return;

    // Validate phone format first
    if (!validatePhoneFormat(employerPhone)) {
      setError("employerPhone", {
        type: "format",
        message: "Phone number must be exactly 10 digits"
      });
      return;
    }

    // Check for duplicate phones
    if (checkPhoneEquality(employerPhone, cellPhone, "personal") || 
        checkPhoneEquality(employerPhone, homePhone, "home")) {
      return;
    }

    setLoadingButton(true);

    try {
      trimWhiteSpaceObjValue(data);
      
      const workPhone = employerPhone.replace(/[()-\s_]/g, "");
      const lead_id = sessionStorage.getItem("lead_id");
      
      const res = await updateContact(dispatch, contactRedux?._id, {
        lead_id, 
        workPhone, 
        step: 16
      });

      if (res?.status === 200) {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setLoadingButton(false);
            navigate("/deposit");
          }
        }, nextStepDelayTime);
      } else {
        setLoadingButton(false);
        // Handle API error
        setError("employerPhone", {
          type: "api",
          message: "Failed to update contact. Please try again."
        });
      }
    } catch (error) {
      setLoadingButton(false);
      setError("employerPhone", {
        type: "api",
        message: "An error occurred. Please try again."
      });
    }
  };

  // Debounced validation effect
  useEffect(() => {
    if (!employerPhone) {
      clearErrors("employerPhone");
      return;
    }

    const timeoutId = setTimeout(() => {
      // Clear previous errors first
      clearErrors("employerPhone");
      
      // Then check for duplicates
      checkPhoneEquality(employerPhone, homePhone, "home") ||
      checkPhoneEquality(employerPhone, cellPhone, "personal");
    }, 300); // Debounce validation

    return () => clearTimeout(timeoutId);
  }, [employerPhone, homePhone, cellPhone, checkPhoneEquality, clearErrors]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Box className="WorkPhone-page">
      <Navbar />
      <ScrollToTop />
      <Box
        sx={{
          width: "100%",
          maxWidth: "700px",
          margin: "20px auto"
        }}
      >
        <div className="step-main">
          <form className="step-form" onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                width: "100%",
                margin: "10px auto 30px",
                padding: isMobile && "0 15px"
              }}
            >
              <Progress step={15} />
            </Box>
            <Box sx={{ textAlign: "center", marginBottom: "11px" }}>
              <img
                style={{
                  maxWidth: isMobile && "42px"
                }}
                src={WorkPhoneIcon}
                alt="Work Phone Icon"
              />
            </Box>
            <h2 className="title">Work Phone Number</h2>
            <h5>
              This phone number is simply used to confirm your employment.
            </h5>
            <div className="form-control">
              <FormHelperText sx={labelCustom}>
                Your work phone number
              </FormHelperText>
              <Controller
                name="employerPhone"
                control={control}
                defaultValue={employerPhone}
                rules={{
                  required: "Phone number is required!",
                  pattern: {
                    value: phonePattern,
                    message: "The phone number is not correct!"
                  }
                }}
                render={({ field: { ref, ...field } }) => (
                  <FormControl
                    fullWidth
                    sx={{
                      ...formControlCustom,
                      marginBottom: "20px"
                    }}
                  >
                    <ReactInputMask
                      {...field}
                      mask="(999)-999-9999"
                      maskChar="_"
                      value={employerPhone}
                      onChange={(e) => {
                        setEmployerPhone(e.target.value);
                        field.onChange(e);
                      }}
                    >
                      {() => (
                        <TextField
                          inputRef={(input) => {
                            if (input) {
                              input.addEventListener("focus", () => {
                                input.setAttribute("inputmode", "numeric");
                              });
                              ref(input);
                            }
                          }}
                          fullWidth
                          sx={textFieldCustom}
                          size={isMobile ? "small" : "medium"}
                          error={!!errors?.employerPhone}
                          helperText={
                            errors?.employerPhone?.message || ""
                          }
                          placeholder="e.g. (123)-123-1234"
                          InputLabelProps={{ shrink: false }}
                        />
                      )}
                    </ReactInputMask>
                  </FormControl>
                )}
              />
            </div>
            <Box
              sx={{
                maxWidth: "474px",
                margin: "0 auto"
              }}
            >
              <StepBtn
                prevLink={prevLink}
                onSubmit={handleSubmit(onSubmit)}
                loading={loadingButton}
              />
            </Box>
          </form>
          <StepPreFooter />
        </div>
      </Box>
    </Box>
  );
};

export default WorkPhone;